import { useCallback, useEffect, useRef, useState } from 'react';
import { openWidgetPopoutWindow } from '../utils/popoutWindow.utils';

// Owns whether a widget is popped out to its own window, plus a handle to
// that window for focusing it from the dashboard placeholder. When the
// feature becomes unavailable (e.g. viewport drops below desktop width),
// the widget automatically returns to its dashboard cell.
export default function useWidgetPopout({ isEnabled = true } = {}) {
  const [popoutWindow, setPopoutWindow] = useState(null);
  const [isOpening, setIsOpening] = useState(false);
  const isMountedRef = useRef(true);
  const isEnabledRef = useRef(isEnabled);
  isEnabledRef.current = isEnabled;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isEnabled && popoutWindow) {
      setPopoutWindow(null);
    }
  }, [isEnabled, popoutWindow]);

  const popOutWidget = useCallback(async () => {
    if (!isEnabledRef.current || isOpening || popoutWindow) return;

    setIsOpening(true);
    let openedWindow = null;
    try {
      openedWindow = await openWidgetPopoutWindow();
    } catch (error) {
      console.error('Failed to open widget pop-out window:', error);
    }

    if (!isMountedRef.current) {
      openedWindow?.close();
      return;
    }

    setIsOpening(false);
    if (!isEnabledRef.current) {
      openedWindow?.close();
      return;
    }

    if (openedWindow?.document?.body) {
      setPopoutWindow(openedWindow);
    } else {
      openedWindow?.close();
    }
  }, [isOpening, popoutWindow]);

  const returnToDashboard = useCallback(() => setPopoutWindow(null), []);

  const focusPopoutWindow = useCallback(() => {
    if (popoutWindow && !popoutWindow.closed) {
      popoutWindow.focus();
    }
  }, [popoutWindow]);

  return {
    isPoppedOut: Boolean(popoutWindow),
    isOpening,
    popoutWindow,
    popOutWidget,
    returnToDashboard,
    focusPopoutWindow,
  };
}
